import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import SideDrawer from '../../../SideDrawer';
import InputField from 'components/InputField';
import validations from 'validations';
import { DataRoomSelectableNode } from '../../../FileCheckSelector/types';
import {
  SideDrawerProps,
  SideDrawerRef,
} from '../../../SideDrawer/SideDrawer.component';
import RadioGroupField from 'components/RadioGroupField';
import DatepickerField from 'components/DatepickerField';
import CheckToggleField from 'components/CheckToggleField';
import TextareaEmailsField from 'components/TextareaEmailsField';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import TextAreaField from 'components/TextAreaField';
import PasswordField from 'components/PasswordField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import {
  CreateDataRoomShareableLinkForm,
  CreateDataRoomShareableLinkTemplate,
  DATA_ROOM_SHAREABLE_LINK_ACCESS,
  DATA_ROOM_SHAREABLE_LINK_DESTINATION,
  DATA_ROOM_SHAREABLE_LINK_EXPIRES,
  DataRoomShareableLink,
} from 'models/ShareableLink';
import ResolvePopup from 'components/ResolvePopup';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import {
  getArrayOfEmails,
  getDataRoomRoute,
  getHasElementsInArray,
  getResolveUrl,
  getShareableLinkRoute,
  scrollToErrorsOnSubmit,
} from 'utils';
import {
  createInitialValues,
  createInitialValuesFromLink,
  getPayloadFromFormValues,
  validate,
} from './utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ShareableLinkItems from './components/ShareableLinkItems';
import ShareableLinkCreated from './components/ShareableLinkCreated';
import {
  getIsAccessAddPasword,
  getIsAccessAddSpecificRecipiens,
  getIsAccessAnyone,
  getIsLinkExpiresOther,
} from 'router/subrouters/DataRoom/utils';
import { useNavigate } from 'react-router-dom';
import ShareableLinkNda from './components/ShareableLinkNda';
import DataRoomNdaContext from 'router/subrouters/DataRoom/providers/DataRoomNda/DataRoomNda.context';
import ShareableLinkTermsAndConditions from './components/ShareableLinkTermsAndConditions';
import { EXPIRE_AFTER } from './data';
import useCreateSecureLinkMixpanelProps from './hook';
import ShareableLinkNotifications from './components/ShareableLinkNotifications';
import isEqual from 'lodash/isEqual';

import './ShareableLinkDrawer.styles.scss';

type ShareableLinkDrawerProps = {
  className?: string;
  nodes?: DataRoomSelectableNode[];
  isLoadingNodes?: boolean;
  initiallyCheckedNodes?: string[];
  checkedFileIds?: string[];
  checkedFolderIds?: string[];
  link?: CreateDataRoomShareableLinkTemplate;
  isEdit?: boolean;
  setNodes: React.Dispatch<React.SetStateAction<DataRoomSelectableNode[]>>;
  onSuccessLink?: (link: DataRoomShareableLink) => void;
  // Reopens the waiting room request this link is being created for; only the
  // provider that owns that hand-off passes it.
  onBack?: () => void;
} & SideDrawerProps;

const ShareableLinkDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ShareableLinkDrawerProps
> = (props, ref) => {
  const {
    className,
    link,
    isEdit = false,
    nodes = [],
    isLoadingNodes,
    initiallyCheckedNodes = [],
    checkedFileIds = [],
    checkedFolderIds = [],
    setNodes,
    closeAction,
    onSuccessLink,
    onBack,
    ...rest
  } = props;

  const [url, setUrl] = useState('');

  // bumped on every opening: the drawer keeps its children mounted, so the form
  // has to be remounted for a new link not to start from the previous values.
  // Until the first opening there is nothing to render either
  const [formKey, setFormKey] = useState(0);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const classes = classNames('shareable-link-drawer', className);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
    setDataRoom,
  } = useContext(DataRoomContext);
  const { activeDataRoomNdas } = useContext(DataRoomNdaContext);

  const { t } = useTranslation();

  const commonValidations = useValidations();

  const navigate = useNavigate();

  const createSecureLinkMixpanel = useCreateSecureLinkMixpanelProps();

  const title = t(isEdit ? 'editSecureLink' : 'createSecureLink');

  const expireAfterOptions = useMemo(
    () =>
      Object.values(DATA_ROOM_SHAREABLE_LINK_EXPIRES).map((value) => ({
        value,
        label: t(EXPIRE_AFTER[value]),
      })),
    [t],
  );

  const linkDestinationOptions = useMemo(
    () => [
      {
        value: DATA_ROOM_SHAREABLE_LINK_DESTINATION.WAITING_ROOM,
        label: t('waitingRoom'),
      },
      {
        value: DATA_ROOM_SHAREABLE_LINK_DESTINATION.DIRECT_ACCESS,
        label: t('directAccessToFiles'),
      },
    ],
    [t],
  );

  const initialValues = useMemo(
    () =>
      link
        ? createInitialValuesFromLink(link, activeDataRoomNdas)
        : createInitialValues(),
    [link, activeDataRoomNdas],
  );

  const hasSelectedNodes = useMemo(
    () =>
      getHasElementsInArray(checkedFileIds) ||
      getHasElementsInArray(checkedFolderIds),
    [checkedFileIds, checkedFolderIds],
  );

  const renderForm = useMemo(
    () => (isEdit ? !!link : !!formKey),
    [formKey, isEdit, link],
  );

  const hasRequestToGoBackTo = useMemo(
    () => !!link?.waitingRoomRequestId && !!onBack,
    [link, onBack],
  );

  const onOpen = useCallback(() => setFormKey((old) => old + 1), []);

  const onClose = useCallback(() => {
    setUrl('');
    closeAction?.();
  }, [closeAction]);

  const updateTermsAndConditions = useCallback(
    (data: DataRoomShareableLink) => {
      const { includeTermsAndConditionProtection, termsAndConditionsContent } =
        data;

      if (!includeTermsAndConditionProtection) return;

      setDataRoom((old) => ({ ...old, termsAndConditionsContent }));
    },
    [setDataRoom],
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        const {
          recipientEmailAddresses,
          recipientEmailAddressesUnsubmited,
          waitingRoomRequestId,
        } = values;

        const finalEmails = getArrayOfEmails(
          recipientEmailAddresses,
          recipientEmailAddressesUnsubmited,
        );

        const payload = getPayloadFromFormValues({
          ...values,
          recipientEmailAddresses: finalEmails,
        });

        const payloadData = {
          ...payload,
          fileIds: checkedFileIds,
          folderIds: checkedFolderIds,
        };

        if (isEdit) {
          const { data } = await dataRoomApi.shareableLinks.patchShareableLink(
            link?._id,
            payloadData,
          );

          onSuccessLink?.(data);
          updateTermsAndConditions(data);
          popSuccess(t('secureLinkUpdatedSuccessfully'));
          onClose();
          drawer?.setOpen(false);
          return;
        }

        createSecureLinkMixpanel(payloadData);

        const {
          data: { dataRoomShareableLink },
        } = await dataRoomApi.shareableLinks.createShareableLink(dataRoomId, {
          ...payloadData,
          waitingRoomRequestId,
        });

        onSuccessLink?.(dataRoomShareableLink);
        updateTermsAndConditions(dataRoomShareableLink);

        setUrl(getResolveUrl(getShareableLinkRoute(dataRoomShareableLink._id)));
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoomId,
      isEdit,
      link,
      drawer,
      checkedFileIds,
      checkedFolderIds,
      t,
      onClose,
      createSecureLinkMixpanel,
      onSuccessLink,
      popServerError,
      popSuccess,
      updateTermsAndConditions,
    ],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      title={title}
      openAction={onOpen}
      closeAction={onClose}
      {...rest}
    >
      {url ? (
        <ShareableLinkCreated
          url={url}
          onViewAll={() => {
            drawer.setOpen(false);
            navigate(getDataRoomRoute(dataRoomId, 'shareable-links'));
          }}
        />
      ) : (
        renderForm && (
          <Form<CreateDataRoomShareableLinkForm>
            key={formKey}
            initialValues={initialValues}
            initialValuesEqual={isEqual}
            keepDirtyOnReinitialize
            onSubmit={onSubmit}
            validate={validate}
            render={({ values, submitting, invalid, handleSubmit }) => {
              const {
                linkAccess,
                linkExpires,
                anyoneWithNonPublicDomain,
                recipientEmailAddresses,
                recipientEmailAddressesUnsubmited,
              } = values;

              const isAccessSpecificRecipiens =
                getIsAccessAddSpecificRecipiens(linkAccess);
              const hasSpecificRecipiens =
                isAccessSpecificRecipiens &&
                !!getArrayOfEmails(
                  recipientEmailAddresses,
                  recipientEmailAddressesUnsubmited,
                )?.length;

              return (
                <form onSubmit={handleSubmit}>
                  {submitting && <Loader />}

                  {hasRequestToGoBackTo && (
                    <Button
                      className="shareable-link-drawer__back"
                      type="button"
                      variant="link"
                      icon={ArrowleftIcon}
                      onClick={onBack}
                    >
                      {t('backToRequest')}
                    </Button>
                  )}

                  <ShareableLinkItems
                    isLoading={isLoadingNodes}
                    nodes={nodes}
                    initiallyCheckedNodes={initiallyCheckedNodes}
                    setNodes={setNodes}
                  />

                  <div className="border" />

                  <Field
                    label={t('nameThisLink')}
                    description={t('nameHelpIdentify')}
                    name="name"
                    required
                    component={InputField}
                    validate={validations.required()}
                  />

                  <div className="border" />

                  <p className="subtitle">{t('linkAccess')}</p>

                  <div className="column">
                    <Field<string>
                      name="linkAccess"
                      type="radio"
                      value={DATA_ROOM_SHAREABLE_LINK_ACCESS.ANYONE_WITH_LINK}
                      label={t('anyoneWithTheLink')}
                      component={RadioButtonField}
                    />
                    {getIsAccessAnyone(linkAccess) && (
                      <Field
                        name="recipientEmailAddresses"
                        label={t('emailAddressesInform')}
                        component={TextareaEmailsField}
                      />
                    )}

                    <Field<string>
                      name="linkAccess"
                      type="radio"
                      value={DATA_ROOM_SHAREABLE_LINK_ACCESS.ADD_PASSWORD}
                      label={t('requirePassword')}
                      component={RadioButtonField}
                    />
                    {getIsAccessAddPasword(linkAccess) && (
                      <Field
                        name="password"
                        label={t('password')}
                        withoutStrengthIndicator
                        autoComplete="new-password"
                        component={PasswordField}
                        validate={commonValidations.password}
                        required
                      />
                    )}

                    <Field<string>
                      name="linkAccess"
                      type="radio"
                      value={
                        DATA_ROOM_SHAREABLE_LINK_ACCESS.SPECIFIC_RECIPIENTS
                      }
                      component={RadioButtonField}
                      label={t('specificRecipientsOnly')}
                    />

                    {isAccessSpecificRecipiens && (
                      <>
                        <Field
                          name="recipientEmailAddresses"
                          label={t('recipientEmailAddress')}
                          component={TextareaEmailsField}
                          {...(!anyoneWithNonPublicDomain && {
                            required: true,
                            validate: validations.required(),
                          })}
                        />

                        <Field
                          name="anyoneWithNonPublicDomain"
                          type="checkbox"
                          component={CheckboxField}
                          label={
                            <span>
                              {t('anyoneWithNonPublicDomain')}
                              <ResolvePopup
                                onHover
                                popupContent={t(
                                  'recepientMustBeCustomEmailDomain',
                                )}
                              >
                                <InformationcircleIcon />
                              </ResolvePopup>
                            </span>
                          }
                        />

                        {anyoneWithNonPublicDomain && (
                          <Field
                            label={t('emailDomain')}
                            name="emailDomain"
                            required
                            component={InputField}
                            validate={validations.composeValidators(
                              validations.required(),
                              validations.nonPublicEmailDomainValidation(
                                t('pleaseEnterValidNonPublicEmailDomain'),
                              ),
                            )}
                          />
                        )}

                        <Field
                          name="optionalNote"
                          label={t('includeOptionalNote')}
                          component={TextAreaField}
                          maxLength={1000}
                          rows="4"
                        />
                      </>
                    )}
                  </div>

                  <div className="border" />

                  <div className="column">
                    <RadioGroupField
                      name="linkExpires"
                      label={t('linkExpiresAfter')}
                      options={expireAfterOptions}
                    />

                    {getIsLinkExpiresOther(linkExpires) && (
                      <Field
                        name="linkExpiresDate"
                        component={DatepickerField}
                        onlyFuture
                        required
                        validate={validations.composeValidators(
                          validations.required(),
                          validations.onlyFuture(t('onlyFuture')),
                        )}
                      />
                    )}
                  </div>

                  {!getIsAccessAddSpecificRecipiens(linkAccess) && (
                    <>
                      <div className="border" />
                      <RadioGroupField
                        name="linkDestination"
                        label={t('linkDestination')}
                        options={linkDestinationOptions}
                      />
                    </>
                  )}

                  <div className="border" />

                  <div className="column">
                    <Field
                      name="allowDownloads"
                      type="checkbox"
                      component={CheckToggleField}
                      label={
                        <span>
                          {t('allowDownloads')}
                          <ResolvePopup
                            onHover
                            popupContent={t('allowRecipientsToDownload')}
                          >
                            <InformationcircleIcon />
                          </ResolvePopup>
                        </span>
                      }
                    />
                  </div>

                  <div className="border" />

                  <ShareableLinkNotifications />

                  <div className="border" />

                  <p className="title">{t('accessProtection')}</p>

                  <ShareableLinkTermsAndConditions key={link?._id} />

                  <ShareableLinkNda />

                  <div className="actions">
                    <Button
                      disabled={submitting || !hasSelectedNodes}
                      onClick={() => {
                        scrollToErrorsOnSubmit(invalid, handleSubmit);
                      }}
                    >
                      {t(
                        isEdit
                          ? 'editSecureLink'
                          : hasSpecificRecipiens
                          ? 'sendToRecipients'
                          : 'createSecureLink',
                      )}
                    </Button>
                  </div>
                </form>
              );
            }}
          />
        )
      )}
    </SideDrawer>
  );
};

export default forwardRef(ShareableLinkDrawer);
